import { Fragment, useCallback, useEffect, useRef, useState, type Dispatch } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { createPortal } from 'react-dom';
import { $isCodeHighlightNode } from '@lexical/code';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListItemNode } from '@lexical/list';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $isTableSelection } from '@lexical/table';
import { $getNearestBlockElementAncestorOrThrow, mergeRegister } from '@lexical/utils';
import { styled } from '@mui/material/styles';
import type { LexicalEditor } from 'lexical';
import {
  $createParagraphNode,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';

import { getDOMRangeRect } from '@/components/TextEditorWithLexical/utils/getDOMRangeRect';
import { setFloatingElemPosition } from '@/components/TextEditorWithLexical/utils/setFloatingElemPosition';

import { ReactComponent as DisableFormatSVG } from '../../images/icons/disable-format.svg';
import { ReactComponent as LinkSVG } from '../../images/icons/link.svg';
import { ReactComponent as BoldSVG } from '../../images/icons/type-bold.svg';
import { ReactComponent as ItalicSVG } from '../../images/icons/type-italic.svg';
import { ReactComponent as SubScriptSVG } from '../../images/icons/type-subscript.svg';
import { ReactComponent as SuperScriptSVG } from '../../images/icons/type-superscript.svg';
import { ReactComponent as UnderlineSVG } from '../../images/icons/type-underline.svg';
import { getSelectedNode } from '../../utils/getSelectedNode';

const StyledFloatingTextFormatPopup = styled('div')({
  display: 'flex',
  background: '#fff',
  padding: '4px',
  verticalAlign: 'middle',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 10,
  opacity: 0,
  boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.3)',
  borderRadius: '8px',
  transition: 'opacity 0.5s',
  height: '35px',
  willChange: 'transform',
  'button.popup-item': {
    border: 0,
    display: 'flex',
    background: 'none',
    borderRadius: '10px',
    padding: '8px',
    cursor: 'pointer',
    verticalAlign: 'middle',
    alignItems: 'center',
    color: '#637381',
  },
  'button.popup-item:disabled': {
    cursor: 'not-allowed',
  },
  'button.popup-item.spaced': {
    marginRight: '2px',
  },
  'button.popup-item i.format': {
    backgroundSize: 'contain',
    height: '18px',
    width: '18px',
    marginTop: '2px',
    verticalAlign: '-0.25em',
    display: 'flex',
    opacity: 0.6,
  },
  'button.popup-item:disabled i.format': {
    opacity: 0.2,
  },
  'button.popup-item.active': {
    backgroundColor: 'rgba(223, 232, 250, 0.3)',
  },
  'button.popup-item.active i': {
    opacity: 1,
  },
  '.popup-item:hover:not([disabled])': {
    backgroundColor: '#eee',
  },
  'select.popup-item': {
    border: 0,
    display: 'flex',
    background: 'none',
    borderRadius: '10px',
    padding: '8px',
    verticalAlign: 'middle',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    width: '70px',
    fontSize: '14px',
    color: '#777',
    textOverflow: 'ellipsis',
  },
  'select.code-language': {
    textTransform: 'capitalize',
    width: '130px',
  },
  '.popup-item .text': {
    display: 'flex',
    lineHeight: '20px',
    verticalAlign: 'middle',
    fontSize: '14px',
    color: '#777',
    textOverflow: 'ellipsis',
    width: '70px',
    overflow: 'hidden',
    height: '20px',
    textAlign: 'left',
  },
  '.popup-item .icon': {
    display: 'flex',
    width: '20px',
    height: '20px',
    userSelect: 'none',
    marginRight: '8px',
    lineHeight: '16px',
    backgroundSize: 'contain',
  },
  'i.chevron-down': {
    marginTop: '3px',
    width: '16px',
    height: '16px',
    display: 'flex',
    userSelect: 'none',
  },
  'i.chevron-down.inside': {
    width: '16px',
    height: '16px',
    display: 'flex',
    marginLeft: '-25px',
    marginTop: '11px',
    marginRight: '10px',
    pointerEvents: 'none',
  },
  '.divider': {
    width: '1px',
    backgroundColor: '#eee',
    margin: '0 4px',
  },
});

const TextFormatFloatingToolbar = ({
  editor,
  anchorElem,
  isLink,
  isBold,
  isItalic,
  isUnderline,
  isSubscript,
  isSuperscript,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor;
  anchorElem: HTMLElement;
  isBold: boolean;
  isItalic: boolean;
  isLink: boolean;
  isSubscript: boolean;
  isSuperscript: boolean;
  isUnderline: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element => {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null);

  const insertLink = useCallback(() => {
    if (!isLink) {
      setIsLinkEditMode(true);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, 'https://');
    } else {
      setIsLinkEditMode(false);
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink, setIsLinkEditMode]);

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX;
        const y = e.clientY;
        const elementUnderMouse = document.elementFromPoint(x, y);

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          popupCharStylesEditorRef.current.style.pointerEvents = 'none';
        }
      }
    }
  }

  function mouseUpListener() {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto';
      }
    }
  }

  const clearFormatting = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          if ($isTextNode(node)) {
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }
            const extractedTextNode = extractedNodes[0];
            if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
              textNode = extractedTextNode;
            }

            if (textNode.__style !== '') {
              textNode.setStyle('');
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
            }
          } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
            node.replace($createParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat('');
          }
        });
      }
    });
  }, [editor]);

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener);
      document.addEventListener('mouseup', mouseUpListener);

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener);
        document.removeEventListener('mouseup', mouseUpListener);
      };
    }
  }, [popupCharStylesEditorRef]);

  const $updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection();

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current;
    const nativeSelection = window.getSelection();

    if (popupCharStylesEditorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement);

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem, isLink);
    }
  }, [editor, anchorElem, isLink]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        $updateTextFormatFloatingToolbar();
      });
    };

    window.addEventListener('resize', update);
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [editor, $updateTextFormatFloatingToolbar, anchorElem]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      $updateTextFormatFloatingToolbar();
    });
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateTextFormatFloatingToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateTextFormatFloatingToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateTextFormatFloatingToolbar]);

  return (
    <StyledFloatingTextFormatPopup ref={popupCharStylesEditorRef}>
      {editor.isEditable() && (
        <Fragment>
          <button
            type="button"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            className={'popup-item spaced ' + (isBold ? 'active' : '')}
          >
            <BoldSVG />
          </button>
          <button
            type="button"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            className={'popup-item spaced ' + (isItalic ? 'active' : '')}
          >
            <ItalicSVG />
          </button>
          <button
            type="button"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
            }}
            className={'popup-item spaced ' + (isUnderline ? 'active' : '')}
          >
            <UnderlineSVG />
          </button>
          <button
            type="button"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
            }}
            className={'popup-item spaced ' + (isSubscript ? 'active' : '')}
            title="Subscript"
          >
            <SubScriptSVG />
          </button>
          <button
            type="button"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
            }}
            className={'popup-item spaced ' + (isSuperscript ? 'active' : '')}
            title="Superscript"
          >
            <SuperScriptSVG />
          </button>
          <button type="button" onClick={insertLink} className={'popup-item spaced ' + (isLink ? 'active' : '')}>
            <LinkSVG />
          </button>
          <button onClick={clearFormatting} className="popup-item spaced" title="Clear text formatting" type="button">
            <DisableFormatSVG />
          </button>
        </Fragment>
      )}
    </StyledFloatingTextFormatPopup>
  );
};

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  setIsLinkEditMode: Dispatch<boolean>,
): JSX.Element | null {
  const [isText, setIsText] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isShowToolbar, setIsShowToolbar] = useState(false);

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      if (editor.isComposing()) {
        return;
      }
      const selection = $getSelection();
      const nativeSelection = window.getSelection();
      const rootElement = editor.getRootElement();

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) || rootElement === null || !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false);
        return;
      }

      if (!$isRangeSelection(selection)) {
        return;
      }

      const node = getSelectedNode(selection);

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsSubscript(selection.hasFormat('subscript'));
      setIsSuperscript(selection.hasFormat('superscript'));

      const parent = node.getParent();

      if ($isParagraphNode(parent) || $isListItemNode(parent)) {
        setIsShowToolbar(true);
      } else {
        setIsShowToolbar(false);
      }

      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== '') {
        setIsText($isTextNode(node) || $isParagraphNode(node));
      } else {
        setIsText(false);
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '');
      if (!selection.isCollapsed() && rawTextContent === '') {
        setIsText(false);
        return;
      }
    });
  }, [editor]);

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup);
    return () => {
      document.removeEventListener('selectionchange', updatePopup);
    };
  }, [updatePopup]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updatePopup();
      }),
      editor.registerRootListener(() => {
        if (editor.getRootElement() === null) {
          setIsText(false);
        }
      }),
    );
  }, [editor, updatePopup]);

  if (!isText || !isShowToolbar) {
    return null;
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isLink={isLink}
      isBold={isBold}
      isItalic={isItalic}
      isSubscript={isSubscript}
      isSuperscript={isSuperscript}
      isUnderline={isUnderline}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  );
}

export const FloatingTextFormatToolbarPlugin = ({
  anchorElem = document.body,
  setIsLinkEditMode,
}: {
  anchorElem?: HTMLElement;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();
  return useFloatingTextFormatToolbar(editor, anchorElem, setIsLinkEditMode);
};
